<template>
  <transition name="modal-fade">
    <div v-if="showPreferences" class="cookie-modal-overlay" @click.self="closeModal">
      <div class="cookie-modal">
        <div class="cookie-modal-header">
          <h2>Cookie Preferences</h2>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        
        <div class="cookie-modal-body">
          <p class="modal-description">
            We use cookies and similar technologies to help personalize content, tailor and measure ads, 
            and provide a better experience. You can customize your choices below.
          </p>
          
          <div class="cookie-categories">
            <div 
              v-for="(category, key) in cookieCategories" 
              :key="key"
              class="cookie-category"
            >
              <div class="category-header">
                <div class="category-info">
                  <h3>{{ category.name }}</h3>
                  <p>{{ category.description }}</p>
                </div>
                <div class="category-toggle">
                  <label class="switch" :class="{ disabled: category.required }">
                    <input 
                      type="checkbox" 
                      :checked="getCategoryValue(key)"
                      :disabled="category.required"
                      @change="toggleCategory(key, $event.target.checked)"
                    >
                    <span class="slider"></span>
                  </label>
                  <span v-if="category.required" class="required-label">Always On</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="cookie-modal-footer">
          <button class="btn-modal btn-cancel" @click="closeModal">
            Cancel
          </button>
          <button class="btn-modal btn-save" @click="savePreferences">
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { cookieCategories } from '../services/cookieService'

export default {
  name: 'CookiePreferencesModal',
  data() {
    return {
      cookieCategories
    }
  },
  computed: {
    ...mapState({
      showPreferences: state => state.cookieConsent.showPreferences,
      categories: state => state.cookieConsent.categories
    })
  },
  methods: {
    ...mapActions([
      'saveCookiePreferences',
      'closeCookiePreferences'
    ]),
    ...mapMutations([
      'SET_COOKIE_CATEGORY'
    ]),
    getCategoryValue(key) {
      return this.categories[key] || false
    },
    toggleCategory(category, value) {
      if (!this.cookieCategories[category].required) {
        this.SET_COOKIE_CATEGORY({ category, value })
      }
    },
    closeModal() {
      this.closeCookiePreferences()
    },
    savePreferences() {
      this.saveCookiePreferences()
    }
  }
}
</script>

<style scoped>
.cookie-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.cookie-modal {
  background: white;
  border-radius: 8px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.cookie-modal-header {
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cookie-modal-header h2 {
  margin: 0;
  font-size: 24px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #666;
}

.cookie-modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.modal-description {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 25px;
}

.cookie-categories {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.cookie-category {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
  background: #f9f9f9;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.category-info {
  flex: 1;
}

.category-info h3 {
  margin: 0 0 5px 0;
  font-size: 16px;
  color: #333;
}

.category-info p {
  margin: 0;
  font-size: 13px;
  color: #666;
  line-height: 1.4;
}

.category-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
}

.required-label {
  font-size: 12px;
  color: #28a745;
  font-weight: 500;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch.disabled {
  opacity: 0.6;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #28a745;
}

input:disabled + .slider {
  cursor: not-allowed;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.cookie-modal-footer {
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-modal {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cancel {
  background: #f8f9fa;
  color: #333;
  border: 1px solid #dee2e6;
}

.btn-cancel:hover {
  background: #e2e6ea;
}

.btn-save {
  background: #28a745;
  color: white;
}

.btn-save:hover {
  background: #218838;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .cookie-modal,
.modal-fade-leave-to .cookie-modal {
  transform: scale(0.9);
}

@media (max-width: 576px) {
  .cookie-modal {
    max-height: 90vh;
  }
  
  .category-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .category-toggle {
    margin-top: 10px;
  }
}
</style>